/**
 * Black-Scholes option pricing model implementation
 * Ported from Python backend (contracts/utils/options_analysis.py)
 */

/**
 * Normal cumulative distribution function
 * @param {number} x - Input value
 * @returns {number} CDF value
 */
function normCDF(x) {
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  // Save the sign of x
  const sign = x >= 0 ? 1 : -1;
  x = Math.abs(x) / Math.sqrt(2);

  // A&S formula 7.1.26
  const t = 1.0 / (1.0 + p * x);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

  return 0.5 * (1.0 + sign * y);
}

/**
 * Black-Scholes call option price calculation
 * @param {number} S - Current stock price
 * @param {number} K - Strike price
 * @param {number} T - Time to expiration (in years)
 * @param {number} r - Risk-free rate (as decimal, e.g., 0.05 for 5%)
 * @param {number} sigma - Volatility (as decimal, e.g., 0.3 for 30%)
 * @returns {number} Call option price
 */
export function blackScholesCall(S, K, T, r, sigma) {
  // Input validation to prevent mathematical errors
  if (T <= 0 || sigma <= 0 || S <= 0 || K <= 0) {
    return Math.max(S - K, 0); // Return intrinsic value
  }

  // Prevent extreme values that cause numerical issues
  if (S / K > 1000 || K / S > 1000) {
    // For extremely out-of-the-money options, return intrinsic value
    return Math.max(S - K, 0);
  }

  try {
    const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
    const d2 = d1 - sigma * Math.sqrt(T);

    // Check for numerical overflow/underflow
    if (isNaN(d1) || isNaN(d2) || !isFinite(d1) || !isFinite(d2)) {
      return Math.max(S - K, 0);
    }

    const callPrice = S * normCDF(d1) - K * Math.exp(-r * T) * normCDF(d2);

    // Ensure result is valid
    if (isNaN(callPrice) || !isFinite(callPrice) || callPrice < 0) {
      return Math.max(S - K, 0);
    }

    return callPrice;
  } catch (error) {
    // Fallback to intrinsic value on any mathematical error
    return Math.max(S - K, 0);
  }
}

/**
 * Black-Scholes put option price calculation
 * @param {number} S - Current stock price
 * @param {number} K - Strike price
 * @param {number} T - Time to expiration (in years)
 * @param {number} r - Risk-free rate (as decimal, e.g., 0.05 for 5%)
 * @param {number} sigma - Volatility (as decimal, e.g., 0.3 for 30%)
 * @returns {number} Put option price
 */
export function blackScholesPut(S, K, T, r, sigma) {
  // Input validation to prevent mathematical errors
  if (T <= 0 || sigma <= 0 || S <= 0 || K <= 0) {
    return Math.max(K - S, 0); // Return intrinsic value
  }

  // Prevent extreme values that cause numerical issues
  if (S / K > 1000 || K / S > 1000) {
    // For extremely out-of-the-money options, return intrinsic value
    return Math.max(K - S, 0);
  }

  try {
    const d1 = (Math.log(S / K) + (r + 0.5 * sigma * sigma) * T) / (sigma * Math.sqrt(T));
    const d2 = d1 - sigma * Math.sqrt(T);

    // Check for numerical overflow/underflow
    if (isNaN(d1) || isNaN(d2) || !isFinite(d1) || !isFinite(d2)) {
      return Math.max(K - S, 0);
    }

    const putPrice = K * Math.exp(-r * T) * normCDF(-d2) - S * normCDF(-d1);

    // Ensure result is valid
    if (isNaN(putPrice) || !isFinite(putPrice) || putPrice < 0) {
      return Math.max(K - S, 0);
    }

    return putPrice;
  } catch (error) {
    // Fallback to intrinsic value on any mathematical error
    return Math.max(K - S, 0);
  }
}

/**
 * Calculate Black-Scholes option price based on option type
 * @param {number} S - Current stock price
 * @param {number} K - Strike price
 * @param {number} T - Time to expiration (in years)
 * @param {number} r - Risk-free rate (as decimal)
 * @param {number} sigma - Volatility (as decimal)
 * @param {string} optionType - 'call' or 'put'
 * @returns {number} Option price
 */
export function blackScholes(S, K, T, r, sigma, optionType) {
  if (optionType === 'call') {
    return blackScholesCall(S, K, T, r, sigma);
  } else if (optionType === 'put') {
    return blackScholesPut(S, K, T, r, sigma);
  } else {
    throw new Error(`Invalid option type: ${optionType}`);
  }
}

/**
 * Recalculate simulation values for a screener row
 * @param {Object} row - Screener result row
 * @param {number} daysToGain - Days to target (user input)
 * @param {number} underlyingGainPct - Expected stock movement % (user input)
 * @param {number} riskFreeRate - Risk-free rate (default 0.05)
 * @param {number|null} baselinePremium - Optional override for the % / P&L baseline.
 *   When null (screener default), the current market premium is used. The watchlist
 *   passes the user's avg cost when the global baseline toggle is in "avg_cost" mode.
 * @returns {Object} Updated row with recalculated simulation fields
 */
export function recalculateSimulation(row, daysToGain, underlyingGainPct, riskFreeRate = 0.05, baselinePremium = null) {
  // Extract necessary fields
  const underlyingPrice = row['Underlying Price'];
  const strike = row['Strike'];
  const daysUntilExp = row['Days Until Expiration'];
  const currentPremium = row['Current Premium'];
  const impliedVolatility = row['Implied Volatility'] / 100; // Convert from percentage
  const optionType = row['Option Type']?.toLowerCase();
  const positionType = row['Position Type']?.toLowerCase();

  // Baseline for % gain / P&L: fall back to current premium when missing or non-positive
  const effectiveBaseline = (Number.isFinite(baselinePremium) && baselinePremium > 0)
    ? baselinePremium
    : currentPremium;

  // Validate inputs
  if (!underlyingPrice || !strike || !daysUntilExp || !optionType) {
    console.warn('Missing required fields for recalculation:', row);
    return row;
  }

  // Calculate simulated values
  const simulatedStock = underlyingPrice * (1 + underlyingGainPct / 100);
  const timeToTarget = Math.max((daysUntilExp - daysToGain) / 365, 0.0001);

  // Calculate simulated premium using Black-Scholes
  const simulatedPremium = blackScholes(
    simulatedStock,
    strike,
    timeToTarget,
    riskFreeRate,
    impliedVolatility,
    optionType
  );

  // Calculate premium gain percentage - different for buy vs sell positions
  const isSell = positionType === 'sell';
  let premiumGain = 0;
  let returnOnCollateral = null;
  let collateralPerContract = null;

  if (effectiveBaseline > 0) {
    if (isSell) {
      // SELL: Profit when option premium DECREASES
      premiumGain = ((effectiveBaseline - simulatedPremium) / effectiveBaseline) * 100;

      // Calculate collateral and return on collateral for sells.
      // ROC is anchored to the premium *actually received* when the position
      // was opened — that's the current market premium at run time, not the
      // user's avg cost — so it does not honor the baseline toggle.
      if (optionType === 'put') {
        // Cash-secured put: need strike × 100 per contract
        collateralPerContract = strike * 100;
      } else {
        // Naked call: approximate margin (20% of underlying + premium)
        collateralPerContract = (underlyingPrice * 0.20 + currentPremium) * 100;
      }

      const premiumReceived = currentPremium * 100;
      returnOnCollateral = collateralPerContract > 0
        ? (premiumReceived / collateralPerContract) * 100
        : null;
    } else {
      // BUY: Profit when option premium INCREASES
      premiumGain = ((simulatedPremium - effectiveBaseline) / effectiveBaseline) * 100;
    }
  }

  // Helper function for annualized return calculation
  const calcAnnualized = (baseReturn, days) => {
    if (days === null || days === undefined || days <= 0 || baseReturn === null || baseReturn === undefined) {
      return null;
    }
    try {
      return (Math.pow(1 + baseReturn / 100, 365 / days) - 1) * 100;
    } catch (e) {
      return null;
    }
  };

  // Determine base return based on position type
  const baseReturn = isSell ? returnOnCollateral : premiumGain;

  // Calculate both annualized versions
  const annReturnTarget = calcAnnualized(baseReturn, daysToGain);
  const annReturnExpiry = calcAnnualized(baseReturn, daysUntilExp);

  // Calculate equity values if allocation exists
  const allocation = row['Allocated Equity'];
  const simulatedEquity = allocation
    ? allocation * (1 + premiumGain / 100)
    : null;

  // Build updated row
  let updatedRow = {
    ...row,
    'Days to Gain': daysToGain,
    'Underlying Gain %': underlyingGainPct,
    'Simulated Underlying': simulatedStock,
    'Simulated Premium': simulatedPremium,
    'Premium % Gain': premiumGain,
    'Ann. Return (Target)': annReturnTarget !== null ? annReturnTarget : 'NA',
    'Ann. Return (Expiry)': annReturnExpiry !== null ? annReturnExpiry : 'NA',
    'Simulated Equity': simulatedEquity
  };

  // Update sell-specific fields if this is a sell position
  if (isSell && currentPremium > 0) {
    const premiumReceived = currentPremium * 100;

    updatedRow['Collateral Required'] = collateralPerContract;
    updatedRow['Return on Collateral %'] = returnOnCollateral;
    updatedRow['Max Profit'] = premiumReceived;

    if (optionType === 'put') {
      updatedRow['Max Loss'] = (strike - currentPremium) * 100;
    } else {
      updatedRow['Max Loss'] = 'Unlimited';
    }

    // Simulated P&L for sell positions — uses the toggle-resolved baseline so
    // "avg cost" mode reflects the user's realized entry, not today's market.
    const numContracts = row['Number of Contracts'] || 1;
    updatedRow['Simulated P&L'] = (effectiveBaseline - simulatedPremium) * numContracts * 100;
  }

  return updatedRow;
}

/**
 * Batch recalculate simulations for multiple rows
 * @param {Array} rows - Array of screener result rows
 * @param {number} daysToGain - Days to target (user input)
 * @param {number} underlyingGainPct - Expected stock movement % (user input)
 * @returns {Array} Array of updated rows
 */
export function batchRecalculateSimulations(rows, daysToGain, underlyingGainPct) {
  return rows.map(row => recalculateSimulation(row, daysToGain, underlyingGainPct));
}

/**
 * Get the maximum days to target from a set of results
 * @param {Array} results - Array of screener results
 * @returns {number} Minimum days until expiration across all results
 */
export function getMaxDaysToTarget(results) {
  if (!results || results.length === 0) return 365;

  const expirationDays = results
    .map(row => row['Days Until Expiration'])
    .filter(days => days && days > 0);

  if (expirationDays.length === 0) return 365;

  return Math.min(...expirationDays);
}

/**
 * Find the underlying price at which P&L = 0 using binary search over Black-Scholes.
 * @param {number} strike - Strike price
 * @param {number} currentPremium - Premium paid/received at entry
 * @param {number} T - Time remaining at exit (in years), e.g., (DTE - daysToExit) / 365
 * @param {number} iv - Implied volatility as decimal (e.g., 0.30)
 * @param {string} optionType - 'call' or 'put'
 * @param {boolean} isSell - true for sell/write positions, false for buy
 * @param {number} riskFreeRate - Risk-free rate (default 0.05)
 * @returns {number|null} Breakeven underlying price, or null if inputs are invalid
 */
export function findBreakevenPrice(strike, currentPremium, T, iv, optionType, isSell, riskFreeRate = 0.05) {
  if (!strike || !currentPremium || !iv || iv <= 0 || !optionType) return null;

  const safeT = Math.max(T, 0.0001);
  let low = strike * 0.01;
  let high = strike * 12;

  for (let i = 0; i < 50; i++) {
    const mid = (low + high) / 2;
    const premium = blackScholes(mid, strike, safeT, riskFreeRate, iv, optionType);
    const pnl = isSell ? (currentPremium - premium) : (premium - currentPremium);

    if (Math.abs(pnl) < 0.001) return mid;

    if (optionType === 'call') {
      isSell ? (pnl > 0 ? (low = mid) : (high = mid)) : (pnl < 0 ? (low = mid) : (high = mid));
    } else {
      isSell ? (pnl > 0 ? (high = mid) : (low = mid)) : (pnl < 0 ? (high = mid) : (low = mid));
    }
  }
  return (low + high) / 2;
}